/**
 * 법령별 AI 가이드 — 수출입요령/세번상세 요건의 "설명글".
 *   GET /api/onto/law-guide?law=… (온디맨드 생성·캐시). 원문은 관세청/law.go.kr 안내(CLHS 무복사).
 */
import { useState, useEffect } from "react";
import { ChevronRight, ChevronDown, ExternalLink } from "lucide-react";

// 경량 마크다운 — ## 소제목 / - 리스트 / 문단. (외부 의존 없이)
function renderGuide(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((raw, i) => {
      const l = raw.trimEnd();
      if (l.startsWith("## "))
        return (
          <div key={i} style={{ fontWeight: 700, fontSize: 13, marginTop: 10, color: "var(--audit-text)" }}>
            {l.slice(3)}
          </div>
        );
      if (l.startsWith("# "))
        return (
          <div key={i} style={{ fontWeight: 800, fontSize: 14, marginTop: 8, color: "var(--audit-text)" }}>
            {l.slice(2)}
          </div>
        );
      if (l.startsWith("- ") || l.startsWith("* "))
        return (
          <div key={i} style={{ fontSize: 12.5, lineHeight: 1.7, paddingLeft: 12, color: "var(--audit-text)" }}>
            · {l.slice(2)}
          </div>
        );
      if (l.startsWith("※"))
        return (
          <div key={i} style={{ fontSize: 11.5, lineHeight: 1.6, marginTop: 8, color: "var(--audit-text-muted)" }}>
            {l}
          </div>
        );
      if (!l) return <div key={i} style={{ height: 4 }} />;
      return (
        <div key={i} style={{ fontSize: 12.5, lineHeight: 1.7, color: "var(--audit-text)" }}>
          {l}
        </div>
      );
    });
}

const lawGovUrl = (law) => `https://www.law.go.kr/법령/${encodeURIComponent(law)}`;

// 가이드 본문(자동 로드 + 렌더 + 원문 링크) — 토글 없이 바로 표시. 마스터-디테일 우측 등 재사용.
export function LawGuideContent({ law }) {
  const [st, setSt] = useState({ loading: true, guide: null, err: null });
  useEffect(() => {
    let alive = true;
    setSt({ loading: true, guide: null, err: null });
    fetch(`/api/onto/law-guide?law=${encodeURIComponent(law)}`)
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then((d) => alive && setSt({ loading: false, guide: d.guide || "", err: null }))
      .catch(
        (e) =>
          alive &&
          setSt({ loading: false, guide: null, err: (e && e.error) || "가이드를 불러오지 못했습니다." }),
      );
    return () => {
      alive = false;
    };
  }, [law]);
  if (st.loading) return <div style={muted}>가이드 불러오는 중… (처음 조회 시 몇 초)</div>;
  if (st.err) return <div style={muted}>{st.err}</div>;
  return (
    <div>
      {renderGuide(st.guide)}
      <a
        href={lawGovUrl(law)}
        target="_blank"
        rel="noreferrer"
        style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 12, fontSize: 12, color: "var(--audit-accent)", textDecoration: "none" }}
      >
        law.go.kr 원문 <ExternalLink size={12} />
      </a>
    </div>
  );
}

// 토글 버튼 + 펼침 본문(세번상세·조회형 요건에서 인라인 사용).
export default function LawGuide({ law }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 5 }}>
      <button onClick={() => setOpen((o) => !o)} style={btn} title={`${law} AI 가이드`}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        AI 가이드 · {law}
      </button>
      {open && (
        <div style={panel}>
          <LawGuideContent law={law} />
        </div>
      )}
    </div>
  );
}

const btn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
  color: "var(--audit-accent)",
  borderRadius: 6,
  padding: "3px 9px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
const panel = {
  marginTop: 6,
  padding: "10px 12px",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-subtle, var(--audit-bg-elevated))",
};
const muted = { fontSize: 12.5, color: "var(--audit-text-muted)", lineHeight: 1.6 };
